import React from "react";
import { Redirect, Route, RouteComponentProps, Switch } from "react-router-dom";

import { navigation } from "@origin-digital/reporting-client";

import { pageRoutes } from "src/consts";
import { usePrevious } from "src/hooks";
import { Loader } from "src/components/Loader";
import { Address } from "@origin-digital/address-lookup";
import { ManageScreen } from "../../screens/Manage/ManageScreen";
import {
  LandlineSuccessRemoveScreen,
  UpdateHomePhoneSuccessScreen,
  UpdateLandlineScreen,
  UpdateLandlineSuccessScreen,
  UpdateMobileScreen,
  UpdateMobileSuccessScreen,
} from "../../screens/UpdatePhone";
import {
  SuccessScreen as UpdateDobSuccessScreen,
  UpdateDobScreen,
} from "../../screens/UpdateDob";
import {
  SuccessScreen as UpdateEmailSuccessScreen,
  UpdateEmailScreen,
} from "../../screens/UpdateEmail";
import {
  AddressProp,
  SuccessScreen as UpdateAddressSuccessScreen,
  UpdateAddressScreen,
} from "../../screens/UpdateAddress";
import { UpdateContactPrefsScreen } from "../../screens/UpdateContactPrefsScreen";
import { useProfileFieldMapChooser } from "../../crm/ProfileFieldMapContext";
import {
  ContactPreferenceRefetchFieldFunction,
  FieldUpdateHook,
  RefetchFieldFunction,
} from "../../crm/profileFieldMap";
import { ContactPreferenceProp } from "../../screens/UpdateContactPrefsScreen/ContactOption";
import { UpdateHomePhoneScreen } from "../../screens/UpdatePhone/UpdateHomePhoneScreen/UpdateHomePhoneScreen";
import { ValidEditableFieldRoute } from "./ValidEditableFieldRoute";
import { hasRouteChanged } from "./utils";
import { SharedEditableFieldRoute } from "./SharedEditableFieldRoute";

import { JointAccountHolderRoute } from "./JointAccountHolderRoute";

export const App: React.FC<RouteComponentProps> = (props) => {
  const prevProps = usePrevious<RouteComponentProps>(props);
  const {
    location: { href, pathname },
  } = window;

  const { useProfileFieldMap, metadata } = useProfileFieldMapChooser();
  const { fieldMap, refetch, error, loading } = useProfileFieldMap();

  if (error) {
    throw error;
  }

  if (hasRouteChanged(prevProps, props)) {
    navigation("screen", {
      currentUri: href,
      path: pathname,
      friendlyUri: `${pathname}:${metadata.type}`,
    });
  }

  if (loading || !fieldMap) {
    return <Loader isLoading={true} variant="page" />;
  }

  return (
    <Switch>
      <Route exact path={pageRoutes.jointAccountHolder}>
        <JointAccountHolderRoute />
      </Route>
      {/* Mobile */}
      <ValidEditableFieldRoute
        exact
        path={pageRoutes.updateMobile.form}
        fieldMap={fieldMap.MOBILE}
        type={metadata.type}
        screen={(data, useUpdate) => (
          <UpdateMobileScreen
            mobile={data as string}
            useUpdate={useUpdate as FieldUpdateHook<string>}
            type={metadata.type}
          />
        )}
      />
      <Route exact path={pageRoutes.updateMobile.success}>
        <UpdateMobileSuccessScreen />
      </Route>

      {/* Landline */}
      {/* Home phone */}
      <SharedEditableFieldRoute
        exact
        path={pageRoutes.updateLandline.form}
        type={metadata.type}
        routes={[
          {
            fieldMap: fieldMap.LANDLINE,
            screen: (data, useUpdate) => (
              <UpdateLandlineScreen
                landline={data as string}
                useUpdate={useUpdate as FieldUpdateHook<string>}
                type={metadata.type}
              />
            ),
          },
          {
            fieldMap: fieldMap.HOME_PHONE,
            screen: (data, useUpdate) => (
              <UpdateHomePhoneScreen
                homephone={data as string}
                useUpdate={useUpdate as FieldUpdateHook<string>}
                type={metadata.type}
              />
            ),
          },
        ]}
      />
      <Route exact path={pageRoutes.updateLandline.success}>
        {fieldMap.LANDLINE ? (
          <UpdateLandlineSuccessScreen />
        ) : (
          <UpdateHomePhoneSuccessScreen />
        )}
      </Route>
      <Route exact path={pageRoutes.updateLandline.removeSuccess}>
        <LandlineSuccessRemoveScreen />
      </Route>

      {/* Date of birth */}
      <ValidEditableFieldRoute
        exact
        path={pageRoutes.updateDob.form}
        fieldMap={fieldMap.DOB}
        type={metadata.type}
        screen={(_, useUpdate) => (
          <UpdateDobScreen
            useUpdate={useUpdate as FieldUpdateHook<string>}
            type={metadata.type}
          />
        )}
      />
      <Route exact path={pageRoutes.updateDob.success}>
        <UpdateDobSuccessScreen />
      </Route>

      {/* Email */}
      <ValidEditableFieldRoute
        exact
        path={pageRoutes.updateEmail.form}
        fieldMap={fieldMap.EMAIL}
        type={metadata.type}
        screen={(data, useUpdate) => (
          <UpdateEmailScreen
            email={data as string}
            useUpdate={useUpdate as FieldUpdateHook<string>}
            type={metadata.type}
          />
        )}
      />
      <Route exact path={pageRoutes.updateEmail.success}>
        <UpdateEmailSuccessScreen />
      </Route>

      <ValidEditableFieldRoute
        exact
        path={pageRoutes.updateAddress.form}
        fieldMap={fieldMap.BILLING_ADDRESS}
        type={metadata.type}
        screen={(data, useUpdate) => (
          <UpdateAddressScreen
            addresses={data as AddressProp[]}
            useUpdate={useUpdate as FieldUpdateHook<Address>}
            type={metadata.type}
          />
        )}
      />
      {/* Address */}
      <Route exact path={pageRoutes.updateAddress.success}>
        <UpdateAddressSuccessScreen />
      </Route>

      {/*  Contact Preferences */}
      <ValidEditableFieldRoute
        exact
        path={pageRoutes.contactPreferences.form}
        fieldMap={fieldMap.CONTACT_PREFERENCE}
        type={metadata.type}
        screen={(data, useUpdate, type) => (
          <UpdateContactPrefsScreen
            contacts={data as ContactPreferenceProp[]}
            useUpdate={useUpdate as FieldUpdateHook<ContactPreferenceProp>}
            refetch={refetch as ContactPreferenceRefetchFieldFunction}
            type={type}
            hasActiveBroadband={metadata.hasActiveBroadband}
          />
        )}
      />

      {/* Manage */}
      <Route exact path={pageRoutes.base}>
        <ManageScreen
          fieldMap={fieldMap}
          refetch={refetch as RefetchFieldFunction}
          hasLPG={metadata.hasLPG}
          hasLPGOnly={metadata.hasLPGOnly}
        />
      </Route>

      {/* Not found */}
      <Redirect to={pageRoutes.base} />
    </Switch>
  );
};
